import React, { useMemo, useState } from 'react';

import { yup } from 'shared/core/yup';
import Form from 'src/components/forms/form';
import { StepHeader, StepPanel, StepFooter, StepButton } from '../shared-components';
import {
  BooleanRadio,
  CustomRadio,
  FreeTextInput,
  ConditionalFieldWrapper,
  SubmitButton,
  GoodCannaFooter,
} from './med-intake-ui';
import { ConditionFollowUps } from './good-canna-v2-follow-ups';
import { GOOD_CANNA_V2_CONDITIONS, ConditionQuestionSet } from './good-canna-v2-conditions';
import { ConditionGrid, ConditionTile, ReviewSummary, ReviewRow, ScrollableStepContent } from './good-canna-v2-styles';
import { GoodCannaFieldsType } from './good-canna-step';
import { goodCannaHealthHistorySchema, REQUIRED_TEXT } from './good-canna-schema';

type GoodCannaHealthHistoryFieldsType = Omit<GoodCannaFieldsType, 'medicalCondition'>;

type GoodCannaStepV2Props = {
  onNext: (data: { goodCanna: GoodCannaFieldsType }) => void;
  onBack: () => void;
};

type SubStep = 'answerFollowUps' | 'healthHistory' | 'review' | 'selectCondition';

export const GoodCannaStepV2 = ({ onNext, onBack }: GoodCannaStepV2Props): JSX.Element => {
  const [subStep, setSubStep] = useState<SubStep>('selectCondition');
  const [selectedCondition, setSelectedCondition] = useState<ConditionQuestionSet | null>(null);
  const [answerIndexes, setAnswerIndexes] = useState<number[]>([]);
  const [healthHistoryAnswers, setHealthHistoryAnswers] = useState<GoodCannaHealthHistoryFieldsType | null>(null);

  const followUpsValidationSchema = useMemo(() => {
    if (!selectedCondition) {
      return undefined;
    }

    return yup.object().shape(
      selectedCondition.questions.reduce<Record<string, yup.StringSchema>>((shape, question) => {
        shape[question.id] = yup.string().required(REQUIRED_TEXT);
        return shape;
      }, {})
    );
  }, [selectedCondition]);

  const handleFollowUpsSubmit = (values: Record<string, string>): void => {
    if (!selectedCondition) {
      return;
    }

    const orderedIndexes = selectedCondition.questions.map((question) => Number(values[question.id]));
    setAnswerIndexes(orderedIndexes);
    setSubStep('review');
  };

  const handleSelectCondition = (conditionQuestionSet: ConditionQuestionSet): void => {
    if (selectedCondition?.condition !== conditionQuestionSet.condition) {
      // Switching conditions invalidates any follow-up answers already recorded.
      setAnswerIndexes([]);
    }

    setSelectedCondition(conditionQuestionSet);
  };

  const handleHealthHistorySubmit = (values: GoodCannaHealthHistoryFieldsType): void => {
    if (!selectedCondition) {
      return;
    }

    setHealthHistoryAnswers(values);

    onNext({
      goodCanna: {
        ...values,
        medicalCondition: selectedCondition.condition,
        // Additive GoodCanna V2 fields (FINTECH-4403) -- medicalConditions carries
        // the single selected condition (there is no secondary-condition concept in
        // this flow), and diseaseQuestionAnswers is its follow-up answers, in the
        // same order as selectedCondition.questions. Both were previously tracked
        // in local state for the review screen but never actually sent.
        medicalConditions: [selectedCondition.condition],
        diseaseQuestionAnswers: answerIndexes,
      } as GoodCannaFieldsType,
    });
  };

  if (subStep === 'selectCondition') {
    return (
      <>
        <StepHeader
          onBack={onBack}
          showBackButton
          stepHeading='Medical questionnaire'
          stepDescription='Choose your condition, then answer its follow-up questions.'
        />
        <StepPanel>
          <ScrollableStepContent>
            <ConditionGrid>
              {GOOD_CANNA_V2_CONDITIONS.map((conditionQuestionSet) => (
                <ConditionTile
                  key={conditionQuestionSet.condition}
                  selected={selectedCondition?.condition === conditionQuestionSet.condition}
                  onClick={() => handleSelectCondition(conditionQuestionSet)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectCondition(conditionQuestionSet);
                    }
                  }}
                  tabIndex={0}
                  role='button'
                >
                  {conditionQuestionSet.condition}
                </ConditionTile>
              ))}
            </ConditionGrid>
          </ScrollableStepContent>
        </StepPanel>
        <StepFooter>
          <StepButton type='button' disabled={!selectedCondition} onClick={() => setSubStep('answerFollowUps')}>
            Continue
          </StepButton>
          <GoodCannaFooter />
        </StepFooter>
      </>
    );
  }

  if (subStep === 'answerFollowUps' && selectedCondition) {
    return (
      <>
        <StepHeader
          onBack={() => setSubStep('selectCondition')}
          showBackButton
          stepHeading={selectedCondition.condition}
          stepDescription='All follow-up questions are required.'
        />
        <Form onSubmit={handleFollowUpsSubmit} validationSchema={followUpsValidationSchema} shouldUnregister>
          <StepPanel>
            <ScrollableStepContent>
              <ConditionFollowUps conditionQuestionSet={selectedCondition} />
            </ScrollableStepContent>
          </StepPanel>
          <StepFooter>
            <SubmitButton>Continue</SubmitButton>
            <GoodCannaFooter />
          </StepFooter>
        </Form>
      </>
    );
  }

  if (subStep === 'healthHistory') {
    return (
      <>
        <StepHeader
          onBack={() => setSubStep('review')}
          showBackButton
          stepHeading='Health history'
          stepDescription='Accurate health information is crucial for the doctor to make informed & effective medical decisions regarding your care.'
        />
        <Form
          onSubmit={handleHealthHistorySubmit}
          validationSchema={goodCannaHealthHistorySchema}
          defaultValues={healthHistoryAnswers ?? undefined}
          shouldUnregister
        >
          <StepPanel>
            <ScrollableStepContent>
              <BooleanRadio question='Are you physically disabled?' name='physicallyDisabled' />

              <CustomRadio
                question='What was your gender at birth?'
                name='gender'
                options={[
                  { label: 'Male', value: 'Male' },
                  { label: 'Female', value: 'Female' },
                ]}
                defaultValue={healthHistoryAnswers?.gender}
              />
              <ConditionalFieldWrapper watchFieldName='gender' showWhen='Female'>
                <BooleanRadio question='Are you currently pregnant?' name='currentlyPregnant' />
                <BooleanRadio question='Are you currently breastfeeding?' name='currentlyBreastfeeding' />
              </ConditionalFieldWrapper>

              <BooleanRadio
                question='Do you currently have, or have you ever had, schizophrenia, psychosis, or any suicidal thoughts or ideation?'
                name='mentalConditions'
                defaultValue={healthHistoryAnswers?.mentalConditions}
              />
              <ConditionalFieldWrapper watchFieldName='mentalConditions'>
                <BooleanRadio
                  question='Do you have a history of suicidal thoughts and ideations?'
                  name='historyOfSuicide'
                />
                <FreeTextInput
                  question='Please list any mental health conditions you have been diagnosed with'
                  name='listMentalHealthConditions'
                  placeholder='Enter your mental health conditions'
                />
                <FreeTextInput
                  question='Please list any current medications you are taking'
                  name='mentalMedications'
                  placeholder='Enter your current medications'
                />
              </ConditionalFieldWrapper>

              <BooleanRadio question='Have you been diagnosed with bipolar disorder?' name='bipolar' />
              <ConditionalFieldWrapper watchFieldName='bipolar'>
                <BooleanRadio question='Is your condition stable?' name='bipolarStable' />
                <FreeTextInput
                  question='Are you taking any medications for Bipolar disorder: if so, which ones?'
                  name='bipolarMedications'
                  placeholder='Enter your medications'
                />
                <FreeTextInput
                  question='Who is currently managing your bipolar disorder?'
                  name='bipolarManager'
                  placeholder='Enter your care provider'
                />
                <BooleanRadio
                  question='Do you understand and acknowledge the potential risks of using medical marijuana while having Bipolar Disorder, including the possibility of worsening mood episodes?'
                  name='bipolarRisk'
                />
              </ConditionalFieldWrapper>

              <BooleanRadio
                question='Are you currently taking any opioids or pain medications?'
                name='opioidsOrLithium'
              />
              <ConditionalFieldWrapper watchFieldName='opioidsOrLithium'>
                <FreeTextInput
                  question='Please explain the reason for taking pain medication'
                  name='opioidsOrLithiumReason'
                  placeholder='Enter your reason for taking pain medication'
                />
                <FreeTextInput
                  question='Please list which type of doctor is prescribing'
                  name='opioidsOrLithiumDoctor'
                  placeholder='Enter your type of doctor'
                />
              </ConditionalFieldWrapper>

              <BooleanRadio
                question='Have you been hospitalized or had any surgeries in the last 6 months?'
                name='hospitalizedOrSurguries'
              />
              <ConditionalFieldWrapper watchFieldName='hospitalizedOrSurguries'>
                <FreeTextInput
                  question='Please explain the hospitalization or surgery'
                  name='recentHospitalizationReason'
                  placeholder='Enter your reason for being hospitalized'
                />
              </ConditionalFieldWrapper>

              <BooleanRadio
                question='Are you currently taking any prescription medications?'
                name='takingMedications'
              />
              <ConditionalFieldWrapper watchFieldName='takingMedications'>
                <FreeTextInput
                  question='Please list which prescription medications you are taking'
                  name='listOfMedications'
                  placeholder='Enter your prescription medications'
                />
              </ConditionalFieldWrapper>

              <BooleanRadio question='Do you have any allergies to medications' name='medAllergies' />
              <ConditionalFieldWrapper watchFieldName='medAllergies'>
                <FreeTextInput
                  question='Please list which medications you are allergic to'
                  name='listOfAllergies'
                  placeholder='Enter your allergies'
                />
              </ConditionalFieldWrapper>

              <BooleanRadio question='Do you currently use tobacco products?' name='tobaccoUse' />
              <ConditionalFieldWrapper watchFieldName='tobaccoUse'>
                <FreeTextInput
                  question='Please list which tobacco products you use and how often'
                  name='howOftenTobacco'
                  placeholder='Enter your tobacco products'
                />
              </ConditionalFieldWrapper>

              <BooleanRadio question='Do you currently drink alcohol?' name='alcoholUse' />
              <ConditionalFieldWrapper watchFieldName='alcoholUse'>
                <FreeTextInput
                  question='Which alcohol products and how often?'
                  name='howOftenAlcohol'
                  placeholder='Enter your alcohol products used'
                />
              </ConditionalFieldWrapper>

              <BooleanRadio
                question='Are you currently using any cannabis products?'
                name='currentMedicalCannabisUse'
              />
            </ScrollableStepContent>
          </StepPanel>
          <StepFooter>
            <SubmitButton>Continue</SubmitButton>
            <GoodCannaFooter />
          </StepFooter>
        </Form>
      </>
    );
  }

  return (
    <>
      <StepHeader
        onBack={() => setSubStep('answerFollowUps')}
        showBackButton
        stepHeading='Review your answers'
        stepDescription='Confirm your responses before continuing.'
      />
      <StepPanel>
        <ScrollableStepContent>
          <ReviewSummary>
            <ReviewRow>
              <strong>Condition:</strong> {selectedCondition?.condition}
            </ReviewRow>
            {selectedCondition?.questions.map((question, index) => (
              <ReviewRow key={question.id}>
                <strong>{question.question}</strong>
                <span>{question.options[answerIndexes[index]]}</span>
              </ReviewRow>
            ))}
          </ReviewSummary>
        </ScrollableStepContent>
      </StepPanel>
      <StepFooter>
        <StepButton type='button' onClick={() => setSubStep('healthHistory')}>
          Continue
        </StepButton>
        <GoodCannaFooter />
      </StepFooter>
    </>
  );
};
